import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";

export interface StatusLogItem {
  id: string;
  mac: string;
  create_date: string;
  updater_version: string | null;
  main_app_version: string | null;
  error_message: string | null;
  error_occurred_at: string | null;
  status: string;
  check: "0" | "1";
}

export interface StatusLogDetailItem {
  statusLogId?: number;
  id: string;
  status: string;
  createDate: string;
  test: boolean;
}

interface PaginatedResponse<T> {
  items: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface StatusLogListResponse extends PaginatedResponse<StatusLogItem> {
  summary: {
    total: number;
    normal: number;
    warning: number;
  };
}

const PAGE_SIZE = 10;
const DETAIL_PAGE_SIZE = 10;

const dateFormat = (date: string) => {
  const now = new Date();
  const target = new Date(date);
  const year = target.getFullYear();
  const month = String(target.getMonth() + 1).padStart(2, "0");
  const day = String(target.getDate()).padStart(2, "0");
  const hour = String(target.getHours()).padStart(2, "0");
  const minute = String(target.getMinutes()).padStart(2, "0");

  if (now.getTime() - target.getTime() < 86400000 && now.getDate() === target.getDate()) {
    return `오늘 ${hour}:${minute}`;
  }
  return `${year}-${month}-${day} ${hour}:${minute}`;
};

const dateFormatSecond = (date: string) => {
  const target = new Date(date);
  const year = target.getFullYear();
  const month = String(target.getMonth() + 1).padStart(2, "0");
  const day = String(target.getDate()).padStart(2, "0");
  const hour = String(target.getHours()).padStart(2, "0");
  const minute = String(target.getMinutes()).padStart(2, "0");
  const second = String(target.getSeconds()).padStart(2, "0");

  return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
};

const useStatusLogList = () => {
  const [date, setDateState] = useState(1);
  const [type1, setType1State] = useState("main");
  const [type2, setType2State] = useState("1");
  const [macSearch, setMacSearchState] = useState("");
  const [updaterVersion, setUpdaterVersionState] = useState("all");
  const [updaterVersions, setUpdaterVersions] = useState<string[]>([]);
  const [mainAppVersion, setMainAppVersionState] = useState("all");
  const [mainAppVersions, setMainAppVersions] = useState<string[]>([]);
  const [statusFilter, setStatusFilterState] = useState("all");
  const [list, setList] = useState<StatusLogItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [summaryTotalCount, setSummaryTotalCount] = useState(0);
  const [normalCount, setNormalCount] = useState(0);
  const [warningCount, setWarningCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedDevice, setSelectedDevice] = useState<StatusLogItem | null>(null);
  const [detailList, setDetailList] = useState<StatusLogDetailItem[]>([]);
  const [detailPage, setDetailPage] = useState(1);
  const [detailTotalCount, setDetailTotalCount] = useState(0);
  const [detailTotalPages, setDetailTotalPages] = useState(1);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [detailErrorMessage, setDetailErrorMessage] = useState("");
  const listRequestId = useRef(0);
  const detailRequestId = useRef(0);

  const fetchData = useCallback(
    async (
      selectedDate: number,
      selectedType1: string,
      selectedType2: string,
      selectedPage: number,
      selectedMacSearch: string,
      selectedUpdaterVersion: string,
      selectedMainAppVersion: string,
      selectedStatusFilter: string,
    ) => {
      const requestId = ++listRequestId.current;
      setIsLoading(true);
      setErrorMessage("");

      try {
        const searchParams = new URLSearchParams({
          date: String(selectedDate),
          type1: selectedType1,
          type2: selectedType2,
          page: String(selectedPage),
          limit: String(PAGE_SIZE),
        });
        if (selectedMacSearch) searchParams.set("mac", selectedMacSearch);
        if (selectedUpdaterVersion !== "all") {
          searchParams.set("updaterVersion", selectedUpdaterVersion);
        }
        if (selectedMainAppVersion !== "all") {
          searchParams.set("mainAppVersion", selectedMainAppVersion);
        }
        if (selectedStatusFilter !== "all") {
          searchParams.set("statusFilter", selectedStatusFilter);
        }
        const response = await fetch(
          `${process.env.REACT_APP_URL}/data/statusLogList?${searchParams.toString()}`,
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const jsonData: StatusLogListResponse | StatusLogItem[] = await response.json();
        if (requestId !== listRequestId.current) return;

        if (Array.isArray(jsonData)) {
          const warning = jsonData.filter((item) => item.check === "1").length;
          setList(jsonData.slice((selectedPage - 1) * PAGE_SIZE, selectedPage * PAGE_SIZE));
          setTotalCount(jsonData.length);
          setSummaryTotalCount(jsonData.length);
          setNormalCount(jsonData.length - warning);
          setWarningCount(warning);
          setTotalPages(Math.max(1, Math.ceil(jsonData.length / PAGE_SIZE)));
          return;
        }

        setList(jsonData.items);
        setTotalCount(jsonData.pagination.total);
        setSummaryTotalCount(jsonData.summary.total);
        setTotalPages(jsonData.pagination.totalPages);
        setNormalCount(jsonData.summary.normal);
        setWarningCount(jsonData.summary.warning);
      } catch {
        if (requestId !== listRequestId.current) return;
        setList([]);
        setTotalCount(0);
        setSummaryTotalCount(0);
        setNormalCount(0);
        setWarningCount(0);
        setTotalPages(1);
        setErrorMessage("서울버스 로그를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.");
      } finally {
        if (requestId === listRequestId.current) setIsLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchData(date, type1, type2, page, macSearch, updaterVersion, mainAppVersion, statusFilter);
  }, [date, fetchData, macSearch, mainAppVersion, page, statusFilter, type1, type2, updaterVersion]);

  useEffect(() => {
    const fetchVersions = async () => {
      try {
        const [updaterResponse, mainAppResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_URL}/data/statusLogVersions`),
          fetch(`${process.env.REACT_APP_URL}/data/statusLogMainAppVersions`),
        ]);
        if (updaterResponse.ok) {
          const jsonData = await updaterResponse.json();
          if (Array.isArray(jsonData)) setUpdaterVersions(jsonData);
        }
        if (mainAppResponse.ok) {
          const jsonData = await mainAppResponse.json();
          if (Array.isArray(jsonData)) setMainAppVersions(jsonData);
        }
      } catch {
        setUpdaterVersions([]);
        setMainAppVersions([]);
      }
    };

    fetchVersions();
  }, []);

  const setDate = (value: number) => {
    setPage(1);
    setDateState(value);
  };

  const setType1 = (value: string) => {
    setPage(1);
    setType1State(value);
  };

  const setType2 = (value: string) => {
    setPage(1);
    setType2State(value);
  };

  const setMacSearch = (value: string) => {
    setPage(1);
    setMacSearchState(value.trim());
  };

  const setUpdaterVersion = (value: string) => {
    setPage(1);
    setUpdaterVersionState(value);
  };

  const setMainAppVersion = (value: string) => {
    setPage(1);
    setMainAppVersionState(value);
  };

  const setStatusFilter = (value: string) => {
    setPage(1);
    setStatusFilterState(value);
  };

  const openDetailModal = useCallback((device: StatusLogItem) => {
    setSelectedDevice(device);
    setDetailList([]);
    setDetailPage(1);
    setDetailTotalCount(0);
    setDetailTotalPages(1);
    setDetailErrorMessage("");
  }, []);

  const closeDetailModal = useCallback(() => {
    detailRequestId.current += 1;
    setSelectedDevice(null);
    setDetailList([]);
    setDetailPage(1);
    setDetailTotalCount(0);
    setDetailTotalPages(1);
    setDetailErrorMessage("");
    setIsDetailLoading(false);
  }, []);

  useEffect(() => {
    if (!selectedDevice) return;
    const requestId = ++detailRequestId.current;

    const fetchDetail = async () => {
      setIsDetailLoading(true);
      setDetailErrorMessage("");
      try {
        const response = await fetch(
          `${process.env.REACT_APP_URL}/data/statusLogDetail?id=${encodeURIComponent(selectedDevice.id)}&mac=${encodeURIComponent(selectedDevice.mac)}&type1=${encodeURIComponent(type1)}&page=${detailPage}&limit=${DETAIL_PAGE_SIZE}`,
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const jsonData: PaginatedResponse<StatusLogDetailItem> | StatusLogDetailItem[] = await response.json();
        if (requestId !== detailRequestId.current) return;

        if (Array.isArray(jsonData)) {
          setDetailList(jsonData.slice((detailPage - 1) * DETAIL_PAGE_SIZE, detailPage * DETAIL_PAGE_SIZE));
          setDetailTotalCount(jsonData.length);
          setDetailTotalPages(Math.max(1, Math.ceil(jsonData.length / DETAIL_PAGE_SIZE)));
          return;
        }

        setDetailList(jsonData.items);
        setDetailTotalCount(jsonData.pagination.total);
        setDetailTotalPages(jsonData.pagination.totalPages);
      } catch {
        if (requestId !== detailRequestId.current) return;
        setDetailList([]);
        setDetailTotalCount(0);
        setDetailTotalPages(1);
        setDetailErrorMessage("디바이스 상세 로그를 불러오지 못했습니다.");
      } finally {
        if (requestId === detailRequestId.current) setIsDetailLoading(false);
      }
    };

    fetchDetail();
  }, [detailPage, selectedDevice, type1]);

  const downloadExcel = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_URL}/data/downloadStatusLog`,
        {},
        { responseType: "blob" },
      );
      const blob = new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "statusLog.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setErrorMessage("엑셀 파일을 생성하지 못했습니다.");
    }
  };

  return {
    date,
    setDate,
    type1,
    setType1,
    type2,
    setType2,
    macSearch,
    setMacSearch,
    updaterVersion,
    setUpdaterVersion,
    updaterVersions,
    mainAppVersion,
    setMainAppVersion,
    mainAppVersions,
    statusFilter,
    setStatusFilter,
    list,
    paginatedList: list,
    totalCount,
    summaryTotalCount,
    normalCount,
    warningCount,
    isLoading,
    errorMessage,
    page,
    setPage,
    totalPages,
    pageSize: PAGE_SIZE,
    selectedDevice,
    openDetailModal,
    closeDetailModal,
    detailList,
    paginatedDetailList: detailList,
    detailTotalCount,
    isDetailLoading,
    detailErrorMessage,
    detailPage,
    setDetailPage,
    detailTotalPages,
    detailPageSize: DETAIL_PAGE_SIZE,
    dateFormat,
    dateFormatSecond,
    downloadExcel,
  };
};

export default useStatusLogList;
